import { ConfirmationModal } from '@issuemanager/apps/client/components';
import {
  useAllMutations,
  useAllQueries,
  useMonitorDiscardIssueFormLoad,
  useMonitorDiscardIssueFormSubmit,
} from '@issuemanager/apps/client/hooks';
import { useDiscardIssue } from '@issuemanager/client/features/issues';
import { useUsers } from '@issuemanager/client/features/users';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscardIssueFormValues, useDiscardIssueForm } from './hooks';

type Props = {
  code: string;
  open: boolean;
  onClose: () => void;
};

function DiscardIssueDialog({ code, open, onClose }: Props) {
  const { t } = useTranslation('issue');
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(false);
  const monitorDiscardIssueFormLoad = useMonitorDiscardIssueFormLoad();
  const users = useUsers({ enabled: open, handleError: true });
  const { form, cleanForm, fields } = useDiscardIssueForm();

  const handleDiscardSuccess = () => {
    onClose();
    cleanForm();
    enqueueSnackbar(t('manage.discard.success'), {
      variant: 'info',
    });
  };

  useAllQueries({
    queries: [users],
    onStart: () => {
      monitorDiscardIssueFormLoad.start();
    },
    onSuccess: () => {
      monitorDiscardIssueFormLoad.stop();
    },
    onError: () => {
      monitorDiscardIssueFormLoad.error();
    },
  });

  const discardIssue = useDiscardIssue({
    code,
    onSuccess: handleDiscardSuccess,
  });

  const monitorDiscardIssue = useMonitorDiscardIssueFormSubmit();

  useAllMutations({
    mutations: [discardIssue],
    onStart: () => {
      monitorDiscardIssue.start();
    },
    onSuccess: () => {
      monitorDiscardIssue.stop();
    },
    onError: () => {
      monitorDiscardIssue.error();
    },
  });

  const onSubmit = async (data: DiscardIssueFormValues) => {
    await discardIssue.mutateAsync({
      usersToNotify: data.usersToNotify.map((user) => user.id),
      message: data.message,
    });
  };

  const handleCancel = () => {
    onClose();
    cleanForm();
  };

  useEffect(() => {
    if (users.isError) {
      setError(true);
    }
  }, [users.isError]);

  return (
    <ConfirmationModal
      isOpen={open && !users.isLoading}
      title={t('manage.discard.title')}
      error={error}
      swapButtons
      description={
        <>
          <Typography mb={2}>{t('manage.discard.description')}</Typography>
          {fields.usersToNotify}
          {fields.message}
        </>
      }
      acceptButtonProps={{
        text: discardIssue.isPending
          ? t('manage.discard.discarding')
          : t('manage.discard.accept'),
        color: 'error',
        loading: discardIssue.isPending,
      }}
      cancelButtonProps={{
        text: t('manage.discard.cancel'),
        variant: 'text',
      }}
      onAccept={form.handleSubmit(onSubmit)}
      onCancel={handleCancel}
    />
  );
}

export default DiscardIssueDialog;
